import React, { useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, Grid, ListItemIcon, makeStyles, Menu, MenuItem, useMediaQuery, useTheme } from "@material-ui/core";
import { IconButton, Typography, Link, Tooltip } from "@material-ui/core";
import { MoreVert, DeleteOutline } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import assetPageStyle from "features/assets/styles";
import { CustomTextField } from "@totalsoft/rocket-webapp-components-deprecated";
import { get, set } from "@totalsoft/change-tracking-react";
import AssetConditionContent from "./AssetConditionContent";
import AssetUsageContent from "./AssetUsageContent";
import AssetDescriptionContent from "./AssetDescriptionContent";
import ActionsDialog from "features/common/components/ActionsDialog";
import AssetSupplierContent from "./AssetSupplierContent";
import { onNumericTextFieldChange } from "utils/propertyChangeAdapters";
import { quoteStatus } from "constants/quoteStatus";
import { getErrors, isValid } from "@totalsoft/pure-validations-react";

const useStyles = makeStyles(assetPageStyle);

function Asset({ quote, index, assetLens, suppliers, onRemoveAsset, editAssetDisabled, validation }) {
  const { t } = useTranslation();

  const theme = useTheme();
  const matchesMD = useMediaQuery(theme.breakpoints.down("md"));
  const [arrowItemHeight, setArrowItemHeight] = useState();
  const styleProps = useMemo(() => ({ matchesMD: matchesMD, arrowItemHeight: arrowItemHeight }), [
    matchesMD,
    arrowItemHeight,
  ]);
  const classes = useStyles(styleProps);

  const [deleteAssetAnchorEl, setDeleteAssetAnchorEl] = useState(null);
  const [assetPopoverAnchorEl, setAssetPopoverAnchorEl] = useState(null);
  const [type, setType] = useState(null);

  const handleClick = useCallback((event) => {
    if (event.currentTarget.id === "asset_button_delete") {
      setDeleteAssetAnchorEl(event.currentTarget);
    } else {
      setType(event.currentTarget.id);
      setAssetPopoverAnchorEl(event.currentTarget);
    }
  }, []);

  const handleClose = useCallback(() => {
    setDeleteAssetAnchorEl(null);
    setAssetPopoverAnchorEl(null);
  }, []);

  const handleRemoveAsset = useCallback(() => {
    onRemoveAsset(index);
    setDeleteAssetAnchorEl(null);
  }, [onRemoveAsset, index]);

  const handleRefChange = (node) => {
    if (node?.clientHeight) setArrowItemHeight(node.clientHeight);
  };

  return (
    <Card id={"assetCard" + (assetLens.quoteAssetId |> get)} className={classes.card}>
      <Grid container>
        <Grid item xs={12} lg={2} className={classes.productSection} ref={handleRefChange}>
          <div className={classes.arrowTipItem}>
            <Grid container direction="column" alignContent="flex-start" alignItems="flex-start">
              <Grid item xs>
                <Typography className={classes.productName}>{assetLens.brandName |> get}</Typography>
              </Grid>
              <Grid item xs>
                <Typography className={classes.scheduleType}>{assetLens.assetName |> get}</Typography>
              </Grid>
              <Grid item xs>
                <Typography className={classes.productName}>{assetLens.kindOfObjectName |> get}</Typography>
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item container xs={6} lg={6} className={classes.padding20}>
          <Grid container className={classes.assetSection}>
            <Grid item container spacing={1} className={classes.paddingTop5}>
              <Grid item xs={2}>
                <Typography variant="caption" className={classes.reducedFont}>
                  {t("Asset.Condition")}
                </Typography>
              </Grid>
              <Grid item xs={10}>
                {editAssetDisabled ? (
                  <Typography variant="caption" className={classes.reducedFont}>
                    {(assetLens.conditionTypeName |> get) || t("General.Empty")}, {t("Asset.YOM") + " "}
                    {(assetLens.manufacturingYear |> get) || t("General.Empty")}
                  </Typography>
                ) : (
                  <Link
                    id="asset_condition"
                    underline="always"
                    color="primary"
                    onClick={handleClick}
                    className={classes.reducedFont}
                  >
                    {(assetLens.conditionTypeName |> get) || t("General.Empty")}, {t("Asset.YOM") + " "}
                    {(assetLens.manufacturingYear |> get) || t("General.Empty")}
                  </Link>
                )}
              </Grid>
            </Grid>
            <Grid item container spacing={1}>
              <Grid item xs={2}>
                <Typography variant="caption" className={classes.reducedFont}>
                  {t("Asset.Usage")}
                </Typography>
              </Grid>
              <Grid item xs={10}>
                {editAssetDisabled ? (
                  <Typography variant="caption" className={classes.reducedFont}>
                    {(assetLens.operatingHours |> get) || t("General.Empty") + " "}
                    {t("Asset.hOfOperation")}
                  </Typography>
                ) : (
                  <Link
                    id="asset_usage"
                    underline="always"
                    color="primary"
                    onClick={handleClick}
                    className={classes.reducedFont}
                  >
                    {(assetLens.operatingHours |> get) || t("General.Empty") + " "}
                    {t("Asset.hOfOperation")}
                  </Link>
                )}
              </Grid>
            </Grid>
            <Grid item container spacing={1}>
              <Grid item xs={2}>
                <Typography variant="caption" className={classes.reducedFont}>
                  {t("Asset.Description")}
                </Typography>
              </Grid>
              <Grid item xs={10}>
                {editAssetDisabled ? (
                  <Typography variant="caption" className={classes.reducedFont}>
                    {(assetLens.assetDescription |> get) || t("General.Empty")}
                  </Typography>
                ) : (
                  <Link
                    id="asset_description"
                    underline="always"
                    color="primary"
                    onClick={handleClick}
                    className={classes.reducedFont}
                  >
                    {(assetLens.assetDescription |> get) || t("General.Empty")}
                  </Link>
                )}
              </Grid>
            </Grid>
            {suppliers.length > 0 && (
              <Grid item container spacing={1} className={classes.paddingBottom5}>
                <Grid item xs={2}>
                  <Typography variant="caption" className={classes.reducedFont}>
                    {t("Asset.Supplier")}
                  </Typography>
                </Grid>
                <Grid item xs={10} className={classes.ellipsisLabel}>
                  {editAssetDisabled ? (
                    <Typography noWrap variant="caption" className={classes.reducedFont}>
                      {(assetLens.supplierName |> get) || t("General.Empty")}
                    </Typography>
                  ) : (
                    <Link
                      id="asset_supplier"
                      underline="always"
                      color="primary"
                      onClick={handleClick}
                      className={classes.reducedFont}
                    >
                      {(assetLens.supplierName |> get) || t("General.Empty")}
                    </Link>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
          <ActionsDialog
            fullWidth={true}
            open={Boolean(assetPopoverAnchorEl)}
            handleClose={handleClose}
            title={t("General.EditValues")}
            initialState={{
              conditionTypeId: assetLens.conditionTypeId |> get,
              conditionTypeName: assetLens.conditionTypeName |> get,
              manufacturingYear: assetLens.manufacturingYear |> get,
              operatingHours: assetLens.operatingHours |> get,
              assetDescription: assetLens.assetDescription |> get,
              suppliers,
              supplierId: assetLens.supplierId |> get,
              supplierName: assetLens.supplierName |> get,
            }}
            Content={
              type === "asset_condition"
                ? AssetConditionContent
                : type === "asset_usage"
                ? AssetUsageContent
                : type === "asset_description"
                ? AssetDescriptionContent
                : AssetSupplierContent
            }
            actions={[
              {
                caption: t("General.Confirm"),
                handle: (state) => {
                  if (type === "asset_condition") {
                    (assetLens.conditionTypeId |> set)(state.conditionTypeId);
                    (assetLens.conditionTypeName |> set)(state.conditionTypeName);
                    (assetLens.manufacturingYear |> set)(state.manufacturingYear);
                  } else if (type === "asset_usage") (assetLens.operatingHours |> set)(state.operatingHours);
                  else if (type === "asset_description") (assetLens.assetDescription |> set)(state.assetDescription);
                  else if (type === "asset_supplier") {
                    (assetLens.supplierId |> set)(state.supplierId);
                    (assetLens.supplierName |> set)(state.supplierName);
                  }
                  return true;
                },
              },
            ]}
          />
        </Grid>

        <Grid item container xs={4} lg={1}>
          <Grid container className={classes.qttySection}>
            <CustomTextField
              label={t("Asset.Qtty")}
              value={assetLens.qtty |> get}
              disabled={editAssetDisabled}
              onChange={assetLens.qtty |> set |> onNumericTextFieldChange}
              isNumeric={true}
              error={!isValid(validation.qtty)}
              helperText={getErrors(validation.qtty)}
            />
          </Grid>
        </Grid>
        <Grid item container xs={6} lg={3} className={classes.priceSection}>
          {quote.showAmountsWithVAT ? (
            <Grid item>
              <CustomTextField
                fullWidth
                label={t("Asset.AssetNetPriceWithVAT")}
                value={assetLens.assetNetPriceWithVAT |> get}
                disabled={editAssetDisabled}
                onChange={assetLens.assetNetPriceWithVAT |> set |> onNumericTextFieldChange}
                isNumeric={true}
                inputProps={{ decimalScale: 2, thousandSeparator: true }}
                startAdornment={<Typography variant="h6">{quote.currencyCode}</Typography>}
                error={!isValid(validation.assetNetPriceWithVAT)}
                helperText={getErrors(validation.assetNetPriceWithVAT)}
                InputLabelProps={{ classes: { root: classes.textFieldLabel } }}
              />
              <CustomTextField
                fullWidth
                label={t("Asset.AdditionalCostsWithVAT")}
                value={assetLens.additionalCostsWithVAT |> get}
                disabled={editAssetDisabled}
                onChange={assetLens.additionalCostsWithVAT |> set |> onNumericTextFieldChange}
                isNumeric={true}
                inputProps={{ decimalScale: 2, thousandSeparator: true }}
                startAdornment={<Typography variant="h6">{quote.currencyCode}</Typography>}
                error={!isValid(validation.additionalCostsWithVAT)}
                helperText={getErrors(validation.additionalCostsWithVAT)}
                InputLabelProps={{ classes: { root: classes.textFieldLabel } }}
              />
            </Grid>
          ) : (
            <Grid item>
              <CustomTextField
                fullWidth
                label={t("Asset.AssetNetPrice")}
                value={assetLens.assetNetPrice |> get}
                disabled={editAssetDisabled}
                onChange={assetLens.assetNetPrice |> set |> onNumericTextFieldChange}
                isNumeric={true}
                inputProps={{ decimalScale: 2, thousandSeparator: true }}
                startAdornment={<Typography variant="h6">{quote.currencyCode}</Typography>}
                error={!isValid(validation.assetNetPrice)}
                helperText={getErrors(validation.assetNetPrice)}
                InputLabelProps={{ classes: { root: classes.textFieldLabel } }}
              />
              <CustomTextField
                fullWidth
                label={t("Asset.AdditionalCosts")}
                value={assetLens.additionalCosts |> get}
                disabled={editAssetDisabled}
                onChange={assetLens.additionalCosts |> set |> onNumericTextFieldChange}
                isNumeric={true}
                inputProps={{
                  decimalScale: 2,
                  thousandSeparator: true,
                }}
                startAdornment={<Typography variant="h6">{quote.currencyCode}</Typography>}
                error={!isValid(validation.additionalCosts)}
                helperText={getErrors(validation.additionalCosts)}
                InputLabelProps={{ classes: { root: classes.textFieldLabel } }}
              />
            </Grid>
          )}
          <Tooltip title={t("General.MoreActions")}>
            <IconButton id="asset_button_delete" onClick={handleClick} size="small" className={classes.moreActionsBtn}>
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu
            id="simple-menu"
            anchorEl={deleteAssetAnchorEl}
            keepMounted
            open={Boolean(deleteAssetAnchorEl)}
            onClose={handleClose}
          >
            <MenuItem
              disabled={editAssetDisabled || quoteStatus.noFinanceProduct.id !== quote.quoteStatusId}
              onClick={handleRemoveAsset}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">{t("General.DeleteItem")}</Typography>
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>
    </Card>
  );
}

Asset.propTypes = {
  quote: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  assetLens: PropTypes.object.isRequired,
  suppliers: PropTypes.array.isRequired,
  onRemoveAsset: PropTypes.func.isRequired,
  editAssetDisabled: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired,
};

export default Asset;
